import { Injectable, inject } from '@angular/core';

import { FixtureType } from '@cds/betting-offer';
import { TagCount, TagType } from '@cds/betting-offer/tags';
import { BatchRequest, FixtureCountsRequest, FixtureState } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { CalendarIntervalKey, IntervalCount, IntervalCountsMap, IntervalFilter, SportIntervalCounts } from '@frontend/sports/calendar/data-access';
import { PrettyUrlsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CountItem, sportModelMethods } from '@frontend/sports/common/core/data-access/sport-model';
import { DateProviderService, getDateForOffset } from '@frontend/sports/common/core/utils/date';
import { first, mapValues, pickBy, sumBy } from 'lodash-es';
import { Observable, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CountIntervalService {
    private bettingService = inject(BettingOfferApi);
    private urlConfig = inject(PrettyUrlsConfig);
    private sitecore = inject(Sitecore);

    intervalStartOfDay(name: string, from: number, to: number, title: string, key: CalendarIntervalKey, trackingSource?: string): IntervalFilter {
        const now = DateProviderService.getDate();
        const today = DateProviderService.getStartOfDay(now);
        const eTitle = trackingSource;
        const filterForDay = this.buildInterval(name, today, from, to, title, key);
        filterForDay.trackingSource = eTitle;

        return filterForDay;
    }

    intervalStartingNow(name: string, from: number, to: number, title: string, key: CalendarIntervalKey, trackingSource?: string): IntervalFilter {
        const now = DateProviderService.getDate();
        const minute = DateProviderService.getStartOfMinute(now);
        const eTitle = trackingSource;
        const filterForTime = this.buildInterval(name, minute, from, to, title, key);
        filterForTime.trackingSource = eTitle;

        return filterForTime;
    }

    getCounts(intervals: { [key: string]: IntervalFilter }): Observable<IntervalCountsMap> {
        return this.getCountsForSport(intervals).pipe(map((response) => response.intervals));
    }

    getCountsForSport(intervals: { [key: string]: IntervalFilter }, sportId?: string): Observable<SportIntervalCounts> {
        /**
         * Whenever app requests ALL counts, live counts can be derived from them
         */
        const canCountLive = intervals['all'] && intervals['live'];
        if (canCountLive) {
            // eslint-disable-next-line no-param-reassign
            delete intervals['live'];
        }
        const params = this.buildRequestParameters(intervals, sportId);

        return this.bettingService.getBatchCounts(params).pipe(
            map((countResponse) => {
                if (!countResponse) {
                    return { intervals: {}, sport: undefined };
                }

                const counts = mapValues(
                    countResponse,
                    (tagCounts, key) =>
                        ({
                            ...intervals[key],
                            count: sumBy(tagCounts, (c) => c.live + (key === 'live' ? 0 : c.preMatch)),
                        }) satisfies IntervalCount,
                );

                if (canCountLive) {
                    counts['live'] = {
                        name: this.urlConfig.translations.live,
                        title: this.sitecore.globalMessages.NavLive,
                        live: true,
                        count: sumBy(countResponse['all'], (c) => c.live),
                        key: 'live',
                    };
                }

                const countsWithValue = pickBy(counts, (c) => c.count > 0) as { [key: string]: IntervalCount };
                const sport = sportId ? this.getSportItem(+sportId, countResponse) : undefined;

                return {
                    intervals: countsWithValue,
                    sport,
                };
            }),
        );
    }

    buildRequestParameters(
        intervals: {
            [key: string]: IntervalFilter;
        },
        sportId?: string,
    ): BatchRequest<FixtureCountsRequest>[] {
        return Object.keys(intervals).map((key) => ({
            batchId: key,
            request: {
                fixtureTypes: FixtureType.Standard,
                state: FixtureState.Latest,
                tagTypes: TagType.Sport,
                extendedTags: TagType.Sport,
                from: intervals[key].from?.toISOString(),
                to: intervals[key].to?.toISOString(),
                sportIds: key === 'all' ? undefined : sportId,
            },
        }));
    }

    private buildInterval(name: string, start: Date, fromOffset: number, toOffset: number, title: string, key: CalendarIntervalKey): IntervalFilter {
        return {
            name,
            from: getDateForOffset(start, fromOffset),
            to: getDateForOffset(start, toOffset),
            title,
            key,
        };
    }

    private getSportItem(sportId: number, tagCountMap: { [key: string]: TagCount[] }): CountItem | undefined {
        /**
         * Filer by sport id is needed because when requesting ALL interval it includes all the sports
         * and we would like to filter it to match sportId; for any other interval response will contain single sport tag
         */
        const sportTag = first(Object.values(tagCountMap))?.filter((tagCount) => tagCount.tag.id === sportId);

        return first(sportModelMethods.fromTag(sportTag));
    }
}
